import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  OnInit,
  output,
  signal,
  TemplateRef,
  untracked,
  viewChild,
} from '@angular/core';
import { cancelableRxResource, debouncedSignal } from '@softline/core';
import {
  ButtonComponent,
  IconComponent,
  InputComponent,
  ItemComponent,
  ItemDescriptionComponent,
  ItemLeadingComponent,
  ItemTitleComponent,
  ItemTrailingComponent,
  LoadingSkeletonComponent,
  ResolvableModal,
  SearchInputComponent,
  SoftSlotDirective,
  SOFTLINE_MODAL_RESOLVER,
  TranslatePipe,
} from '@softline/ui-core';
import { ConstructionSiteOption, ConstructionSiteService, SiteMapThumbComponent } from '@softapps/core/customer-info';
import {
  PhotonService,
  PhotonSuggestion,
} from '../../../../services/photon.service';
import {
  DocumentFormData,
  EMPTY_DOCUMENT_FORM_DATA,
} from '../../../../types/document-form-data';

@Component({
  selector: 'soft-sales-construction-site-step',
  templateUrl: './construction-site-step.component.html',
  styleUrl: './construction-site-step.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'flex flex-col h-full min-w-100' },
  imports: [
    TranslatePipe,
    ItemComponent,
    ItemLeadingComponent,
    ItemTitleComponent,
    ItemDescriptionComponent,
    ItemTrailingComponent,
    IconComponent,
    SearchInputComponent,
    ButtonComponent,
    SoftSlotDirective,
    LoadingSkeletonComponent,
    InputComponent,
    SiteMapThumbComponent,
  ],
})
export class ConstructionSiteStepComponent
  extends ResolvableModal<DocumentFormData>
  implements OnInit
{
  private readonly modalResolver = inject(SOFTLINE_MODAL_RESOLVER, { optional: true });
  private readonly constructionSiteService = inject(ConstructionSiteService);
  private readonly photonService = inject(PhotonService);

  readonly data = input.required<DocumentFormData>();
  readonly isEmbedded = input(false);
  readonly local = signal<DocumentFormData>(EMPTY_DOCUMENT_FORM_DATA);
  readonly next = output<DocumentFormData>();
  readonly dataChange = output<DocumentFormData>();
  readonly create = output<DocumentFormData>();

  override readonly bottomTemplate = viewChild('bottomTpl', { read: TemplateRef });

  readonly search = signal('');
  private readonly debouncedSearch = debouncedSignal(this.search, 200);
  readonly skeletonRows = Array.from({ length: 6 });

  private readonly customerNumber = computed(
    () => this.local().customer?.customerNumber ?? null
  );

  private readonly constructionSitesResource = cancelableRxResource({
    params: () => ({
      customerNumber: this.customerNumber(),
      filter: this.debouncedSearch().trim(),
    }),
    stream: ({ params }) =>
      this.constructionSiteService.query(params.customerNumber, params.filter),
  });

  readonly constructionSites = computed<ConstructionSiteOption[]>(
    () => this.constructionSitesResource.value() ?? []
  );
  readonly loading = computed(
    () => this.constructionSitesResource.status() === 'loading'
  );
  protected readonly selectedKey = computed(() => {
    const id = this.local().constructionSiteId;
    return id != null ? String(id) : null;
  });

  protected readonly filteredConstructionSites = computed(() =>
    this.constructionSites()
  );

  protected readonly mode = signal<'search' | 'manual' | 'create'>('search');
  protected readonly manualMode = computed(() => this.mode() === 'manual');
  protected readonly createMode = computed(() => this.mode() === 'create');
  protected readonly manualName = signal('');

  /**
   * Next free sub-number for the current customer — the highest existing
   * `subnummer` of the loaded construction sites plus one. Falls back to 1
   * when the customer has no construction sites yet.
   */
  protected readonly nextSubNumber = computed(() => {
    const rows = this.constructionSites();
    if (rows.length === 0) return 1;
    const max = rows.reduce(
      (acc, site) => (site.subnummer > acc ? site.subnummer : acc),
      0,
    );
    return max + 1;
  });

  protected readonly createForm = signal({
    bezeichnung: '',
    adresse: '',
  });
  protected readonly isCreating = signal(false);

  // Photon address autocomplete
  private readonly debouncedAdresse = debouncedSignal(
    computed(() => this.createForm().adresse),
    250,
  );
  protected readonly addressSuggestionsOpen = signal(false);
  private readonly addressSuggestionsResource = cancelableRxResource({
    params: () => ({ q: this.debouncedAdresse() }),
    stream: ({ params }) => this.photonService.search(params.q),
  });
  protected readonly addressSuggestions = computed<PhotonSuggestion[]>(
    () => this.addressSuggestionsResource.value() ?? [],
  );
  protected readonly addressSuggestionsLoading = computed(
    () => this.addressSuggestionsResource.status() === 'loading',
  );

  protected readonly canSubmitCreate = computed(
    () => this.createForm().bezeichnung.trim().length > 0,
  );

  protected readonly isManualSelected = computed(
    () => !!this.local().constructionSite?.trim(),
  );

  ngOnInit(): void {
    const seed = untracked(() => this.data());
    this.local.set(seed);
    const existing = seed.constructionSite;
    if (existing != null) {
      this.manualName.set(existing);
      if (existing.trim().length > 0) this.mode.set('manual');
    }
  }

  selectConstructionSite(site: ConstructionSiteOption): void {
    const pickedId = site?.id != null ? Number(site.id) || null : null;
    this.local.update((d) => ({
      ...d,
      constructionSiteId: pickedId,
      constructionSite: null,
    }));
    this.mode.set('search');
    this.dataChange.emit(this.local());
    if (!this.isEmbedded()) {
      this.confirm();
    }
  }

  setMode(next: 'search' | 'manual' | 'create'): void {
    this.mode.set(next);
  }

  toggleManualMode(): void {
    this.mode.update((m) => (m === 'manual' ? 'search' : 'manual'));
  }

  toggleCreateMode(): void {
    this.mode.update((m) => (m === 'create' ? 'search' : 'create'));
  }

  updateCreateField(key: 'bezeichnung' | 'adresse', value: string): void {
    this.createForm.update((f) => ({ ...f, [key]: value }));
    if (key === 'adresse') {
      this.addressSuggestionsOpen.set(value.trim().length >= 3);
    }
  }

  pickAddressSuggestion(s: PhotonSuggestion): void {
    const adresse = [
      [s.street, s.housenumber].filter(Boolean).join(' '),
      [s.postcode, s.city].filter(Boolean).join(' '),
    ]
      .filter((v) => v.trim().length > 0)
      .join(', ');
    this.createForm.update((f) => ({ ...f, adresse }));
    this.addressSuggestionsOpen.set(false);
  }

  closeAddressSuggestions(): void {
    this.addressSuggestionsOpen.set(false);
  }

  async createConstructionSite(): Promise<void> {
    if (!this.canSubmitCreate()) return;
    this.isCreating.set(true);
    try {
      const form = this.createForm();
      // TODO(backend): POST { kundennummer, bezeichnung, adresse } and use the
      //                returned construction site here. For now we construct an
      //                in-memory site so the UI flow is testable end-to-end.
      const created: ConstructionSiteOption = {
        nummer: this.customerNumber() ?? 0,
        subnummer: this.nextSubNumber(),
        kurzbez: form.bezeichnung.trim(),
        '5': form.bezeichnung.trim(),
        '13': form.adresse.trim(),
      } as ConstructionSiteOption;
      this.selectConstructionSite(created);
      this.createForm.set({ bezeichnung: '', adresse: '' });
    } finally {
      this.isCreating.set(false);
    }
  }

  protected onManualInput(event: Event): void {
    const target = event.target as HTMLInputElement | null;
    this.manualName.set(target?.value ?? '');
  }

  applyManualConstructionSite(): void {
    const value = this.manualName().trim();
    if (!value) return;
    this.local.update((d) => ({
      ...d,
      constructionSiteId: null,
      constructionSite: value,
    }));
    this.dataChange.emit(this.local());
    this.confirm();
  }

  rowKey(site: ConstructionSiteOption): string {
    return site.id != null ? String(site.id) : '';
  }

  rowNumberLabel(site: ConstructionSiteOption): string {
    return site.subnummer > 0
      ? `· ${site.nummer}/${site.subnummer}`
      : `· ${site.nummer}`;
  }

  isNoConstructionSite(site: ConstructionSiteOption): boolean {
    return !site.subnummer || site.subnummer === 0;
  }


  rowAddress(site: ConstructionSiteOption): string | null {
    const street = (site['13'] as string | undefined)?.trim();
    const zip = (site['14'] as string | undefined)?.trim();
    const city = (site['15'] as string | undefined)?.trim();
    const cityLine = [zip, city].filter(Boolean).join(' ');
    const result = [street, cityLine].filter(Boolean).join(', ');
    return result.length > 0 ? result : null;
  }

  rowCustomerName(site: ConstructionSiteOption): string {
    return (
      (site['5'] as string | undefined)?.trim() ||
      (site.kurzbez as string | undefined)?.trim() ||
      '—'
    );
  }

  rowConstructionSiteName(site: ConstructionSiteOption): string | null {
    const name1 = (site['10'] as string | undefined)?.trim();
    return name1 || null;
  }

  rowTitle(site: ConstructionSiteOption): string {
    if (this.isNoConstructionSite(site)) return 'Keine Baustelle';
    return this.rowConstructionSiteName(site) ?? this.rowCustomerName(site);
  }

  set(key: keyof DocumentFormData, value: unknown): void {
    this.local.update((d) => ({ ...d, [key]: value }));
    this.dataChange.emit(this.local());
  }

  confirm(): void {
    const result = this.local();
    if (this.modalResolver && !this.isEmbedded()) {
      this.resolve(result);
    } else {
      this.next.emit(result);
    }
  }
}
